import { useState } from 'react';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: formData.name,
          organization: formData.company,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          preferredContact: 'Email'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-navy/10 rounded-md">
            <span className="text-navy text-base font-semibold">Next Steps</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-navy">
            What Happens <span className="text-gold">Next</span>
          </h2>
          <p className="text-lg text-charcoal max-w-3xl mx-auto">
            Getting started is simple. Here's how we work together to help your team embrace AI.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-[#f8f8f9] p-8 rounded-2xl border border-soft-gray/30 shadow-sm hover:shadow-xl transition-all relative">
            <div className="absolute -top-4 -left-4 w-12 h-12 flex items-center justify-center bg-gold text-white font-bold text-xl rounded-full shadow-lg">
              1
            </div>
            <div className="w-16 h-16 flex items-center justify-center bg-navy/10 rounded-xl mb-6 mt-4">
              <i className="ri-phone-line text-3xl text-navy"></i>
            </div>
            <h3 className="text-xl font-bold mb-3 text-navy">Discovery Call</h3>
            <p className="text-charcoal leading-relaxed">
              A 15-minute call to learn about your team, your work, and your goals.
            </p>
          </div>

          <div className="bg-[#f8f8f9] p-8 rounded-2xl border border-soft-gray/30 shadow-sm hover:shadow-xl transition-all relative">
            <div className="absolute -top-4 -left-4 w-12 h-12 flex items-center justify-center bg-gold text-white font-bold text-xl rounded-full shadow-lg">
              2
            </div>
            <div className="w-16 h-16 flex items-center justify-center bg-navy/10 rounded-xl mb-6 mt-4">
              <i className="ri-graduation-cap-line text-3xl text-navy"></i>
            </div>
            <h3 className="text-xl font-bold mb-3 text-navy">Foundations Training</h3>
            <p className="text-charcoal leading-relaxed">
              Your team builds core AI skills they can use immediately in their daily work.
            </p>
          </div>

          <div className="bg-[#f8f8f9] p-8 rounded-2xl border border-soft-gray/30 shadow-sm hover:shadow-xl transition-all relative">
            <div className="absolute -top-4 -left-4 w-12 h-12 flex items-center justify-center bg-gold text-white font-bold text-xl rounded-full shadow-lg">
              3
            </div>
            <div className="w-16 h-16 flex items-center justify-center bg-navy/10 rounded-xl mb-6 mt-4">
              <i className="ri-search-eye-line text-3xl text-navy"></i>
            </div>
            <h3 className="text-xl font-bold mb-3 text-navy">AI Readiness Audit</h3>
            <p className="text-charcoal leading-relaxed">
              An in-depth analysis of your organization and its processes to find room for improvement.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-navy to-[#062299] p-12 rounded-3xl shadow-2xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Ready to Get Started?
          </h3>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Schedule a 15-minute discovery call to see if Foundations is right for your team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://calendly.com/evernorthai/discovery"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Schedule a Discovery Call
            </a>
            <a
              href="mailto:info@evernorthai.com"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
            >
              Not Ready To Talk? Email Us!
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
